import React, { useEffect, useState } from "react";
import axios from "axios";
import { settings } from "../../helpers/settings";
import authHeader from "../../helpers/functions/auth-header";
import { toast } from "../../helpers/swal";
import { Link } from "react-router-dom";
const linkStyle = {
  color: "blue",
  textDecoration: "none",
  fontWeight: 500,
  transition: "all 0.2s ease-in-out",
};

const getTodayDate = () => new Date().toISOString().split("T")[0];

const EmbroideryOrdersPage = () => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(getTodayDate());
  const [notes, setNotes] = useState({});
  const [selectedFolder, setSelectedFolder] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [sortColumn, setSortColumn] = useState("uploaded_time");
  const [allFolders, setAllFolders] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [allFiles, setAllFiles] = useState([]); // 🔍 Arama için tüm veriler
  const [userFiles, setUserFiles] = useState([]); // ✅ Hata engellenir

  useEffect(() => {
    fetchFiles(selectedDate);
    const interval = setInterval(() => {
      fetchFiles(selectedDate);
    }, 900000);
    return () => clearInterval(interval);
  }, [selectedDate]);

  const fetchFiles = async (date) => {
    setLoading(true);
    try {
      const mayaApiUrl = `${settings.apiURL}/embroidery_get_files.php?date=${date}`;
      const customzonApiUrl = `${settings.customzonApiURL}/embroidery_get_files.php?date=${date}`;

      const [mayaResponse, customzonResponse] = await Promise.all([
        axios.get(mayaApiUrl, { headers: authHeader() }).catch((err) => {
          console.error("Maya API Error:", err);
          return { data: [] };
        }),
        axios.get(customzonApiUrl, { headers: authHeader() }).catch((err) => {
          console.error("CustomZon API Error:", err);
          return { data: [] };
        }),
      ]);

      const combinedData = [
        ...(mayaResponse.data || []),
        ...(customzonResponse.data || []),
      ];

      const filteredFiles = combinedData.filter(
        (file) => file.success === 1 && file.deleted === 0
      );

      const filesWithColors = filteredFiles.map((file) => {
        const printed = file.IsPrinted?.trim() || "Not Printed"; // boş ise varsayılan

        return {
          ...file,
          IsPrinted: printed,
          rowColor:
            printed === "Cancelled"
              ? "table-secondary"
              : printed !== "Not Printed"
              ? "table-success"
              : "table-danger",
        };
      });

      setFiles(filesWithColors);

      const folderNames = [
        ...new Set(filteredFiles.map((file) => file.folderName)),
      ];
      setAllFolders(folderNames);

      const initialNotes = {};
      filteredFiles.forEach((file) => {
        initialNotes[file.id] = file.Notes || "";
      });
      setNotes(initialNotes);
    } catch (error) {
      toast("Failed to fetch files", "error");
    } finally {
      setLoading(false);
    }
  };
  const fetchSearchableFiles = async (date) => {
    const startDate = new Date(date);
    startDate.setDate(startDate.getDate() - 10);
    const endDate = new Date(date);
    endDate.setDate(endDate.getDate() + 3);

    const start = startDate.toISOString().split("T")[0];
    const end = endDate.toISOString().split("T")[0];

    try {
      const mayaApiUrl = `${settings.apiURL}/admin_get_files.php?start=${start}&end=${end}`;
      const customzonApiUrl = `${settings.customzonApiURL}/admin_get_files.php?start=${start}&end=${end}`;

      const [mayaResponse, customzonResponse] = await Promise.all([
        axios.get(mayaApiUrl, { headers: authHeader() }).catch((err) => {
          console.error("Maya Search API Error:", err);
          return { data: [] };
        }),
        axios.get(customzonApiUrl, { headers: authHeader() }).catch((err) => {
          console.error("CustomZon Search API Error:", err);
          return { data: [] };
        }),
      ]);

      const combinedData = [
        ...(mayaResponse.data || []),
        ...(customzonResponse.data || []),
      ];

      const filtered = combinedData.filter(
        (file) => file.success === 1 && file.deleted === 0
      );

      const coloredFiles = filtered.map((file) => {
        const printed = file.IsPrinted?.trim() || "Not Printed";
        return {
          ...file,
          IsPrinted: printed,
          rowColor:
            printed === "Cancelled"
              ? "table-secondary"
              : printed !== "Not Printed"
              ? "table-success"
              : "table-danger",
        };
      });

      setAllFiles(coloredFiles);
    } catch (err) {
      toast("Failed to fetch search range files", "error");
    }
  };

  const handlePrinterSelection = async (file, selectedPrinter) => {
    try {
      await axios.post(
        `${settings.apiURL}/admin_update_file.php`,
        {
          file_id: file.file_id,
          is_printed: selectedPrinter,
          notes: file.Notes,
        },
        { headers: authHeader() }
      );

      toast(`Printed on ${selectedPrinter}`, "success");

      setFiles((prevFiles) =>
        prevFiles.map((f) =>
          f.id === file.id
            ? {
                ...f,
                IsPrinted: selectedPrinter,
                rowColor:
                  selectedPrinter === "Cancelled"
                    ? "table-secondary"
                    : selectedPrinter !== "Not Printed"
                    ? "table-success"
                    : "table-danger",
              }
            : f
        )
      );
    } catch (error) {
      toast("Failed to update print status", "error");
    }
  };

  const handleNoteChange = (fileId, newNote) => {
    setNotes({
      ...notes,
      [fileId]: newNote,
    });
  };

  const handleNoteBlur = async (file) => {
    if (file.Notes === notes[file.id]) {
      return;
    }

    try {
      await axios.post(
        `${settings.apiURL}/admin_update_file.php`,
        {
          file_id: file.file_id,
          is_printed: file.IsPrinted,
          notes: notes[file.id],
        },
        { headers: authHeader() }
      );
      toast("Note updated successfully", "success");
    } catch (error) {
      toast("Failed to update note", "error");
    }
  };

  const getDownloadLink = (fileId) => {
    return `https://drive.google.com/uc?id=${fileId}&export=download`;
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortOrder("asc");
    }
  };

  const renderSortArrow = (column) => {
    if (sortColumn !== column) return null;
    return sortOrder === "asc" ? " ↑" : " ↓";
  };
  const formatDateToFolder = (isoDateStr) => {
    const [year, month, day] = isoDateStr.split("-");
    return `${day}-${month}-${year}`; // "04-04-2025"
  };

  const filteredFiles = (searchTerm ? allFiles : files)
    .filter((file) =>
      selectedFolder ? file.folderName === selectedFolder : true
    )
    .filter(
      (file) =>
        file.file_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        file.folderName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (notes[file.id] || "").toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      let aVal = a[sortColumn] || "";
      let bVal = b[sortColumn] || "";

      if (sortColumn === "uploaded_time") {
        return sortOrder === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }
      if (sortColumn === "inch_width" || sortColumn === "inch_height") {
        return sortOrder === "asc"
          ? parseFloat(aVal) - parseFloat(bVal)
          : parseFloat(bVal) - parseFloat(aVal);
      }
      return sortOrder === "asc"
        ? aVal.toString().localeCompare(bVal.toString())
        : bVal.toString().localeCompare(aVal.toString());
    });

  const printedCount = filteredFiles.filter(
    (file) =>
      (file.IsPrinted?.trim() || "Not Printed") !== "Not Printed" &&
      file.IsPrinted !== "Cancelled"
  ).length;

  const notPrintedCount = filteredFiles.filter(
    (file) => (file.IsPrinted?.trim() || "Not Printed") === "Not Printed"
  ).length;

  return (
    <div className="container mt-4">
      {/* 🏠 Home butonu ortalanmış başlıkla birlikte */}
      <div className="text-center mb-4">
        <h2>Embroidery Orders</h2>
        <Link to="/home" className="btn btn-outline-primary mt-2">
          🏠 Home
        </Link>
      </div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2>Embroidery Orders</h2>

        <div className="d-flex align-items-center gap-2">
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="form-control"
          />
          <button
            className="btn btn-primary"
            onClick={() => fetchFiles(selectedDate)}
          >
            Refresh
          </button>
        </div>
      </div>

      <div className="d-flex justify-content-between mb-3">
        <div>
          <label>Filter by Folder: </label>
          <select
            className="form-select"
            value={selectedFolder}
            onChange={(e) => setSelectedFolder(e.target.value)}
          >
            <option value="">All Folders</option>
            {allFolders.map((folder, index) => (
              <option key={index} value={folder}>
                {folder}
              </option>
            ))}
          </select>
        </div>

        <div className="d-flex gap-2 align-items-center">
          <input
            type="text"
            className="form-control"
            placeholder="Search by file, folder, note..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              if (allFiles.length === 0) fetchSearchableFiles(selectedDate);
            }}
          />

          <button
            className="btn btn-secondary"
            onClick={() => handleSort("uploaded_time")}
          >
            Sort by Time {renderSortArrow("uploaded_time")}
          </button>
        </div>
      </div>

      <div className="d-flex justify-content-end mb-2">
        <span className="me-3">
          <strong>Total:</strong> {filteredFiles.length}
        </span>
        <span className="me-3 text-success">
          <strong>Printed:</strong> {printedCount}
        </span>
        <span className="text-danger">
          <strong>Not Printed:</strong> {notPrintedCount}
        </span>
      </div>

      {loading ? (
        <p>Loading files...</p>
      ) : (
        <table className="table table-bordered table-striped">
          <thead>
            <tr>
              <th>#</th>
              <th
                style={{ cursor: "pointer" }}
                onClick={() => handleSort("file_name")}
              >
                File Name{renderSortArrow("file_name")}
              </th>
              <th
                style={{ cursor: "pointer" }}
                onClick={() => handleSort("folderName")}
              >
                Folder Name{renderSortArrow("folderName")}
              </th>
              <th
                style={{ cursor: "pointer" }}
                onClick={() => handleSort("uploaded_date")}
              >
                Uploaded Date{renderSortArrow("uploaded_date")}
              </th>
              <th>Uploaded Folder Date</th>

              <th
                style={{ cursor: "pointer" }}
                onClick={() => handleSort("uploaded_time")}
              >
                Uploaded Time{renderSortArrow("uploaded_time")}
              </th>
              <th>Dimensions (inch)</th>
              <th
                style={{ cursor: "pointer" }}
                onClick={() => handleSort("first_name")}
              >
                Uploaded By{renderSortArrow("first_name")}
              </th>
              <th
                style={{ minWidth: "180px", cursor: "pointer" }}
                onClick={() => handleSort("IsPrinted")}
              >
                Is Printed?{renderSortArrow("IsPrinted")}
              </th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            {filteredFiles.map((file, index) => (
              <tr key={file.id} className={file.rowColor}>
                <td>{index + 1}</td>
                <td>
                  <a
                    href={getDownloadLink(file.file_id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={linkStyle}
                    onMouseEnter={(e) =>
                      (e.target.style.textDecoration = "underline")
                    }
                    onMouseLeave={(e) =>
                      (e.target.style.textDecoration = "none")
                    }
                  >
                    {file.file_name}
                  </a>
                </td>
                <td>{file.folderName}</td>
                <td>{file.uploaded_date}</td>
                <td>{file.drive_date_folder || "-"}</td>

                <td>{file.uploaded_time}</td>
                <td>
                  {file.inch_width.toFixed(1)} x {file.inch_height.toFixed(1)}
                </td>
                <td>
                  {file.first_name} {file.last_name}
                </td>
                <td>
                  <select
                    className="form-select fw-bold"
                    value={file.IsPrinted || "Not Printed"}
                    onChange={(e) =>
                      handlePrinterSelection(file, e.target.value)
                    }
                  >
                    <option value="Not Printed">Not Printed</option>

                    <option value="Embroidery Printer Service">
                      Embroidery Printer Service
                    </option>
                    <option value="Cancelled">Cancelled</option>
                  </select>
                </td>
                <td
                  style={{
                    minWidth: "150px",
                    maxWidth: "250px",
                    wordWrap: "break-word",
                  }}
                >
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={notes[file.id] || ""}
                    onChange={(e) => handleNoteChange(file.id, e.target.value)}
                    onBlur={() => handleNoteBlur(file)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default EmbroideryOrdersPage;
